import { Component, Input, OnInit } from '@angular/core';

import { Property, PropertySet } from '@mirage-projects/cw-models';
import { GisPcvizService } from '@mirage-projects/cw-pcviz-core';

import { ExpressionEvent, FieldExpressionItem } from '../../expression-editor/expression-config';
import { ExpressionEditorService } from '../../expression-editor/expression-editor.service';
import { PropertyBagService } from '../property-bag.service';

/**
 *  This component is used to display a property of type expression in the property bag.
 */
@Component({
    selector: 'cw-property-expression',
    templateUrl: './property-expression.component.html',
    styleUrls: ['./property-expression.component.scss'],
    standalone: false,
})
export class PropertyExpressionComponent implements OnInit {
    /** This object comes from the core and provides methods to obtain and set the value of the properties */
    @Input() propertySet!: PropertySet;

    /** Input field with the required information for this field type */
    @Input() property!: Property;

    label = '';
    placeholder = 'true';
    isValidExpression = true;

    constructor(
        private propertyBagService: PropertyBagService,
        private gisPcvizService: GisPcvizService
    ) {}

    ngOnInit(): void {
        this.label = this.propertyBagService.getCorrectLabel(this.property) ?? '';
        this.initValue();
    }
    /**
     * This method is called when the expression changes.
     * @param expressionEvent The object that represents the expression
     */
    onChange(expressionEvent: ExpressionEvent): void {
        const parsedExpr = expressionEvent.expressionObject;
        this.isValidExpression = expressionEvent.isValid;

        // if it's a constant expression, only true and false are allowed
        if (!parsedExpr || (parsedExpr.constant !== undefined && typeof parsedExpr.constant !== 'boolean')) {
            this.isValidExpression = false;
            return;
        }

        this.property.value = expressionEvent.expression;
        this.propertyBagService.setValueProperty(this.propertySet, this.property);
    }

    /**
     * Returns the fields and types that are available for the expression editor
     */
    getFieldsData(): FieldExpressionItem[] | undefined {
        const layer = this.propertySet;

        const options = {
            onlyVisible: true,
        };

        const vectorLayerUtils = this.gisPcvizService.getVectorLayerUtils();
        if (!vectorLayerUtils) {
            return;
        }

        const fields = vectorLayerUtils.getStringFields(layer.getFeatureDefinition(), layer.getCategory(), options, true);

        const editorFieldIds = ['Id', ...fields.fields];
        const editorFieldTypes = ['IntegerField', ...fields.fieldTypes];

        return ExpressionEditorService.fieldArraysToFieldExpression(editorFieldIds, editorFieldTypes);
    }

    /**
     * Initializes the value of the property
     */
    private initValue(): void {
        this.property.value = this.propertyBagService.getValueProperty(this.propertySet, this.property);
    }
}
